/**
 * Engine Lab — Phase I5.
 * Interactive re-scoring / filtering of the BACKTESTED_INTRADAY population, with
 * TRAIN + VALIDATION metrics shown side-by-side. Promotion gated by validation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info, RotateCcw, Rocket, Zap, Beaker, Radio } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { useTradingStyle } from "@/contexts/TradingStyleContext";

const COMPONENT_KEYS = [
  "vwap_position", "opening_range", "intraday_trend", "momentum",
  "relative_volume", "timeframe_agreement", "day_regime", "liquidity",
];
const COMPONENT_LABELS = {
  vwap_position: "VWAP Position",
  opening_range: "Opening Range",
  intraday_trend: "Intraday Trend",
  momentum: "Momentum",
  relative_volume: "Relative Volume",
  timeframe_agreement: "Timeframe Agreement",
  day_regime: "Day Regime",
  liquidity: "Liquidity",
};
const DEFAULT_WEIGHTS = {
  vwap_position: 15, opening_range: 15, intraday_trend: 20, momentum: 15,
  relative_volume: 15, timeframe_agreement: 10, day_regime: 8, liquidity: 2,
};

function fmtNum(v, digits = 2) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toFixed(digits);
}
function fmtSigned(v, digits = 3) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(digits)}R`;
}

function StatCell({ label, value, tone, testId, sub }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mono text-base font-bold ${tone || "text-slate-100"}`}>{value}</div>
      {sub != null && <div className="mono text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

function SplitPanel({ title, stats, tone, testIdPrefix, highlight }) {
  if (!stats) return null;
  const exp = stats.expectancy_r;
  const pf = stats.profit_factor;
  const expTone = exp == null ? "text-slate-500" : exp > 0 ? "text-emerald-400" : "text-rose-400";
  const pfTone = pf == null ? "text-slate-500" : pf >= 1.05 ? "text-emerald-400" : pf >= 1.0 ? "text-amber-400" : "text-rose-400";
  return (
    <div
      data-testid={testIdPrefix}
      className={`border ${highlight ? "border-cyan-500/60 bg-cyan-500/[0.03]" : "border-[#1E293B] bg-[#0E1523]"} p-4`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`mono text-[10px] uppercase tracking-widest ${tone || "text-slate-400"}`}>{title}</div>
        {highlight && <span className="mono text-[9px] uppercase tracking-widest text-cyan-300 border border-cyan-500/40 px-1 py-0.5">The number that matters</span>}
      </div>
      <div className="grid grid-cols-3 gap-x-4 gap-y-3">
        <StatCell label="Signals" testId={`${testIdPrefix}-signals`}
          value={stats.signal_count ?? 0}
          sub={`${fmtNum(stats.signals_per_day, 2)}/day · ${stats.sessions_covered || 0} sess`} />
        <StatCell label="Fill Rate" testId={`${testIdPrefix}-fill`}
          value={`${fmtNum(stats.fill_rate_pct, 1)}%`} />
        <StatCell label="Win Rate" testId={`${testIdPrefix}-wr`}
          value={`${fmtNum(stats.win_rate_pct, 1)}%`}
          sub={`W:${stats.win_count} L:${stats.loss_count}`} />
        <StatCell label="Profit Factor" testId={`${testIdPrefix}-pf`}
          value={fmtNum(pf, 3)} tone={pfTone} />
        <StatCell label="Expectancy" testId={`${testIdPrefix}-exp`}
          value={fmtSigned(exp)} tone={expTone} />
        <StatCell label="Max DD" testId={`${testIdPrefix}-mdd`}
          value={fmtSigned(stats.max_drawdown_r)} tone="text-rose-400" />
      </div>
      <EquityMini curve={stats.equity_curve || []} testId={`${testIdPrefix}-equity`} />
    </div>
  );
}

function EquityMini({ curve, testId }) {
  if (!curve || curve.length < 2) {
    return <div className="mt-3 mono text-[10px] text-slate-600 border-t border-[#1E293B] pt-2">Insufficient equity data</div>;
  }
  const w = 260, h = 40;
  const ys = curve.map((p) => p.cum_r);
  const min = Math.min(0, ...ys);
  const max = Math.max(0, ...ys);
  const range = Math.max(0.001, max - min);
  const stepX = w / (curve.length - 1);
  const points = curve.map((p, i) => {
    const x = (i * stepX).toFixed(1);
    const y = (h - ((p.cum_r - min) / range) * h).toFixed(1);
    return `${x},${y}`;
  }).join(" ");
  const last = ys[ys.length - 1];
  const stroke = last >= 0 ? "#10b981" : "#f43f5e";
  return (
    <div className="mt-3 border-t border-[#1E293B] pt-2" data-testid={testId}>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Equity Curve (R)</div>
      <svg width={w} height={h} className="w-full">
        <line x1="0" y1={h - ((0 - min) / range) * h} x2={w} y2={h - ((0 - min) / range) * h}
              stroke="#334155" strokeDasharray="2 3" />
        <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" />
      </svg>
      <div className="flex justify-between mono text-[9px] text-slate-500">
        <span>{fmtSigned(min)}</span>
        <span className="text-slate-400">final {fmtSigned(last)}</span>
        <span>{fmtSigned(max)}</span>
      </div>
    </div>
  );
}

function WeightSlider({ label, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-[130px_1fr_50px] items-center gap-3">
      <div className="mono text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <input
        type="range" min="0" max="40" step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="accent-cyan-500 h-1"
      />
      <div className="mono text-xs text-slate-200 tabular-nums text-right">{value}</div>
    </div>
  );
}

function ShadowComparison({ shadow, activeConfig }) {
  const active = activeConfig?.active;
  if (!active || !active.config) {
    return (
      <div className="border border-[#1E293B] bg-[#0E1523] p-4 mt-6" data-testid="shadow-comparison">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-slate-500" />
          <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Shadow Engine · Live vs Baseline</div>
        </div>
        <div className="mono text-[11px] text-slate-500 mt-2">
          Shadow engine is inactive — no config is promoted. Promote a config to start comparing live promoted decisions vs the v1 baseline.
        </div>
      </div>
    );
  }
  const s = shadow || {};
  const p = s.promoted_outcomes || {};
  const b = s.baseline_shadow_outcomes || {};
  const fmt = (v, d = 3) => v == null ? "—" : Number(v).toFixed(d);
  const fmtPct = (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`;
  const expTone = (v) => v == null ? "text-slate-500" : v > 0 ? "text-emerald-400" : "text-rose-400";
  return (
    <div className="border border-cyan-500/30 bg-cyan-500/[0.02] p-4 mt-6" data-testid="shadow-comparison">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-cyan-400" />
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-300">Shadow Engine · Live vs Baseline</div>
          <span className="mono text-[9px] uppercase tracking-widest text-slate-500 border border-slate-700 px-1 py-0.5">
            v1 = {activeConfig?.v1_baseline_version} · promoted = {active.version}
          </span>
        </div>
        <div className="mono text-[10px] text-slate-500">Refreshes every 60s</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="shadow-summary">
        <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Live Decisions Logged</div><div className="mono text-sm text-slate-100" data-testid="shadow-decision-count">{s.decision_count ?? 0}</div></div>
        <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Divergent Decisions</div><div className="mono text-sm text-slate-100" data-testid="shadow-divergent-count">{s.divergent_count ?? 0}</div></div>
        <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Divergence Rate</div><div className="mono text-sm text-cyan-300" data-testid="shadow-divergence-rate">{fmt(s.divergence_rate_pct, 2)}%</div></div>
        <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Min sample</div><div className="mono text-sm text-slate-100">{s.min_sample ?? 30}</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <div className="border border-[#1E293B] p-2" data-testid="shadow-div-aligned"><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Aligned</div><div className="mono text-sm text-slate-100">{s.divergences?.aligned ?? 0}</div></div>
        <div className="border border-[#1E293B] p-2" data-testid="shadow-div-gated"><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Promoted gated baseline</div><div className="mono text-sm text-amber-300">{s.divergences?.promoted_gated_baseline ?? 0}</div></div>
        <div className="border border-[#1E293B] p-2" data-testid="shadow-div-took"><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Baseline WAIT, Promoted took</div><div className="mono text-sm text-cyan-300">{s.divergences?.baseline_wait_promoted_took ?? 0}</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-cyan-500/40 bg-cyan-500/[0.04] p-3" data-testid="shadow-promoted-outcomes">
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2">Promoted (LIVE_INTRADAY)</div>
          {p.insufficient ? (
            <div className="mono text-[11px] text-slate-500" data-testid="shadow-promoted-insufficient">
              Insufficient live data: n={p.n ?? 0} (need ≥ {s.min_sample ?? 30}). Outcomes accumulate as trades mature.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Signals</div><div className="mono text-sm text-slate-100">{p.n}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Win Rate</div><div className="mono text-sm text-cyan-300">{fmtPct(p.win_rate_pct)}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Profit Factor</div><div className="mono text-sm text-slate-100">{fmt(p.profit_factor, 2)}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Expectancy</div><div className={`mono text-sm ${expTone(p.expectancy_r)}`}>{fmt(p.expectancy_r)}R</div></div>
            </div>
          )}
        </div>
        <div className="border border-slate-700 bg-slate-800/20 p-3" data-testid="shadow-baseline-outcomes">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Baseline v1 (SHADOW_INTRADAY)</div>
          {b.insufficient ? (
            <div className="mono text-[11px] text-slate-500" data-testid="shadow-baseline-insufficient">
              Insufficient live data: n={b.n ?? 0} (need ≥ {s.min_sample ?? 30}). Shadow snapshots accumulate when promoted gates a baseline signal.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Signals</div><div className="mono text-sm text-slate-100">{b.n}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Win Rate</div><div className="mono text-sm text-slate-300">{fmtPct(b.win_rate_pct)}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Profit Factor</div><div className="mono text-sm text-slate-100">{fmt(b.profit_factor, 2)}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Expectancy</div><div className={`mono text-sm ${expTone(b.expectancy_r)}`}>{fmt(b.expectancy_r)}R</div></div>
            </div>
          )}
        </div>
      </div>

      <div className="mono text-[10px] text-slate-500 mt-3 leading-relaxed" data-testid="shadow-note">
        {s.note || ""}
      </div>
    </div>
  );
}

export default function EngineLabPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const { style } = useTradingStyle() || {};
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [threshold, setThreshold] = useState(60);
  const [directionFilter, setDirectionFilter] = useState("both");
  const [regimeFilter, setRegimeFilter] = useState("any");
  const [hourFilter, setHourFilter] = useState("any");
  const [minRr, setMinRr] = useState(0);
  const [excludedTickers, setExcludedTickers] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeConfig, setActiveConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [promoting, setPromoting] = useState(false);
  const [shadowData, setShadowData] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, cfg, sh] = await Promise.all([
          api.get("/intraday/lab/meta"),
          api.get("/intraday/lab/config"),
          api.get("/intraday/lab/shadow/comparison").catch(() => ({ data: null })),
        ]);
        setMeta(m.data);
        setActiveConfig(cfg.data);
        setHistory(cfg.data?.history || []);
        setShadowData(sh.data);
      } catch (e) {
        setMetaError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  // Poll shadow comparison every 60s
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const sh = await api.get("/intraday/lab/shadow/comparison");
        setShadowData(sh.data);
      } catch {}
    }, 60_000);
    return () => clearInterval(t);
  }, []);

  // Ctrl/Cmd+K search
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const configBody = useMemo(() => ({
    weights,
    threshold,
    direction_filter: directionFilter,
    regime_filter: regimeFilter,
    hour_filter: hourFilter,
    excluded_tickers: excludedTickers.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
    min_rr: minRr,
  }), [weights, threshold, directionFilter, regimeFilter, hourFilter, excludedTickers, minRr]);

  useEffect(() => {
    if (!meta) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.post("/intraday/lab/evaluate", configBody);
        setResult(r.data);
      } catch (e) {
        setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [configBody, meta]);

  const applyPreset = async () => {
    try {
      const r = await api.get("/intraday/lab/preset/score88_flat");
      const p = r.data;
      setWeights(p.weights);
      setThreshold(p.threshold);
      setDirectionFilter(p.direction_filter);
      setRegimeFilter(p.regime_filter);
      setHourFilter(p.hour_filter);
      setMinRr(p.min_rr);
      setExcludedTickers("");
      toast.success("Preset applied: score>=88 + flat regime");
    } catch (e) { toast.error("Preset failed"); }
  };

  const resetToBaseline = () => {
    setWeights(DEFAULT_WEIGHTS);
    setThreshold(60);
    setDirectionFilter("both");
    setRegimeFilter("any");
    setHourFilter("any");
    setMinRr(0);
    setExcludedTickers("");
  };

  const promote = async () => {
    if (!result?.eligibility?.eligible) return;
    setPromoting(true);
    try {
      const r = await api.post("/intraday/lab/promote", { config: configBody });
      toast.success(`Promoted to ${r.data.version}`);
      const cfg = await api.get("/intraday/lab/config");
      setActiveConfig(cfg.data);
      setHistory(cfg.data?.history || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Promotion failed");
    } finally { setPromoting(false); }
  };

  const rollback = async (version) => {
    try {
      await api.post("/intraday/lab/rollback", { version });
      toast.success(`Rolled back to ${version}`);
      const cfg = await api.get("/intraday/lab/config");
      setActiveConfig(cfg.data);
      setHistory(cfg.data?.history || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Rollback failed");
    }
  };

  const weightSum = Object.values(weights).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen">
      <TopBar onOpenSearch={() => setSearchOpen(true)} />
      <TickerSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <div className="max-w-[1520px] mx-auto px-6 py-6 space-y-6" data-testid="engine-lab">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Beaker className="h-5 w-5 text-cyan-400" />
              <h1 className="mono text-lg uppercase tracking-widest text-slate-100">Engine Lab</h1>
              <span className="mono text-[10px] uppercase tracking-widest text-slate-500 border border-slate-700 px-1.5 py-0.5">Phase I5</span>
            </div>
            <p className="mono text-[11px] text-slate-400 mt-2 max-w-3xl">
              Re-score the BACKTESTED_INTRADAY population under any weight / threshold / filter combination.
              TRAIN and VALIDATION are computed on disjoint session windows.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Active engine version</div>
            <div data-testid="active-version" className="mono text-sm text-cyan-300 font-bold">
              {activeConfig?.active?.version || "—"}
            </div>
            <div className="mono text-[10px] text-slate-500">
              {activeConfig?.active?.config ? "Promoted config" : "v1 baseline (no config)"}
            </div>
          </div>
        </div>

        {/* Scope note */}
        <div className="border border-amber-500/40 bg-amber-500/[0.04] p-3 flex items-start gap-2" data-testid="lab-scope-note">
          <Info className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="mono text-[11px] text-amber-100 leading-relaxed">
            <strong className="text-amber-300">HONESTY SCOPE:</strong> {meta?.scope_note || "Loading…"}
          </div>
        </div>

        {/* Meta banner */}
        {meta && (
          <div className="border border-[#1E293B] bg-[#0E1523] p-3 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="lab-meta">
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Population</div><div className="mono text-sm text-slate-100">{meta.population_size.toLocaleString()}</div></div>
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Sessions</div><div className="mono text-sm text-slate-100">{meta.sessions}</div></div>
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Train Sessions</div><div className="mono text-sm text-slate-100">{meta.train_sessions}</div></div>
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Val Sessions</div><div className="mono text-sm text-slate-100">{meta.validation_sessions}</div></div>
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Window</div><div className="mono text-[11px] text-slate-100">{meta.first_session}<br/>→ {meta.last_session}</div></div>
            <div><div className="mono text-[9px] uppercase tracking-widest text-slate-500">Reconstructed comps</div><div className="mono text-sm text-slate-100">{meta.components_recomputed_count}</div></div>
          </div>
        )}
        {metaError && <div className="mono text-xs text-rose-400 border border-rose-500/40 bg-rose-500/[0.04] p-3" data-testid="lab-meta-error">Failed to load population: {metaError}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <div className="border border-[#1E293B] bg-[#0E1523] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Weights (renorm. to 100)</div>
                <div className="mono text-[10px] text-slate-500">raw sum {weightSum}</div>
              </div>
              <div className="space-y-2">
                {COMPONENT_KEYS.map((k) => (
                  <WeightSlider key={k} label={COMPONENT_LABELS[k]}
                    value={weights[k]}
                    onChange={(v) => setWeights((w) => ({ ...w, [k]: v }))}
                    testId={`weight-${k}`} />
                ))}
              </div>
            </div>

            <div className="border border-[#1E293B] bg-[#0E1523] p-4 space-y-3">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Filters</div>
              <div className="grid grid-cols-[130px_1fr_50px] items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Min Score</div>
                <input type="range" min="50" max="100" step="1" value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  data-testid="filter-threshold" className="accent-cyan-500 h-1" />
                <div className="mono text-xs text-slate-200 tabular-nums text-right">{threshold}</div>
              </div>
              <div className="grid grid-cols-[130px_1fr] items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Direction</div>
                <select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}
                  data-testid="filter-direction"
                  className="bg-[#0B1220] border border-[#1E293B] mono text-xs text-slate-100 py-1.5 px-2">
                  <option value="both">Both</option>
                  <option value="long">Long only</option>
                  <option value="short">Short only</option>
                </select>
              </div>
              <div className="grid grid-cols-[130px_1fr] items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Day Regime</div>
                <select value={regimeFilter} onChange={(e) => setRegimeFilter(e.target.value)}
                  data-testid="filter-regime"
                  className="bg-[#0B1220] border border-[#1E293B] mono text-xs text-slate-100 py-1.5 px-2">
                  <option value="any">Any</option>
                  <option value="flat">Flat only</option>
                  <option value="up">Up only</option>
                  <option value="down">Down only</option>
                </select>
              </div>
              <div className="grid grid-cols-[130px_1fr] items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Hour of Day</div>
                <select value={hourFilter} onChange={(e) => setHourFilter(e.target.value)}
                  data-testid="filter-hour"
                  className="bg-[#0B1220] border border-[#1E293B] mono text-xs text-slate-100 py-1.5 px-2">
                  <option value="any">Any hour</option>
                  <option value="mid_day_only">Mid-day only (11-15 ET)</option>
                  <option value="exclude_open_close">Exclude open/close (11-15 ET)</option>
                </select>
              </div>
              <div className="grid grid-cols-[130px_1fr_50px] items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Min R/R</div>
                <input type="range" min="0" max="3" step="0.1" value={minRr}
                  onChange={(e) => setMinRr(Number(e.target.value))}
                  data-testid="filter-min-rr" className="accent-cyan-500 h-1" />
                <div className="mono text-xs text-slate-200 tabular-nums text-right">{minRr.toFixed(1)}</div>
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">Excluded Tickers (comma-sep)</div>
                <input type="text" value={excludedTickers} onChange={(e) => setExcludedTickers(e.target.value)}
                  placeholder="e.g. TSLA, PLTR"
                  data-testid="filter-excluded-tickers"
                  className="w-full bg-[#0B1220] border border-[#1E293B] mono text-xs text-slate-100 py-1.5 px-2" />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={applyPreset} data-testid="preset-score88-flat"
                className="flex-1 mono text-[10px] uppercase tracking-widest border border-cyan-500/60 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 py-2 flex items-center justify-center gap-2">
                <Zap className="h-3 w-3" /> Preset: score≥88 + flat
              </button>
              <button onClick={resetToBaseline} data-testid="reset-baseline"
                className="mono text-[10px] uppercase tracking-widest border border-slate-600 bg-slate-800/40 text-slate-300 hover:bg-slate-800/70 py-2 px-3 flex items-center justify-center gap-2">
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {error && <div className="mono text-xs text-rose-400 border border-rose-500/40 bg-rose-500/[0.04] p-3" data-testid="lab-error">Evaluation failed: {error}</div>}
            {loading && !result && <div className="mono text-xs text-slate-500 border border-[#1E293B] p-6">Evaluating…</div>}

            {result && (
              <>
                {result.eligibility?.warning && (
                  <div className="border border-rose-500/50 bg-rose-500/[0.06] p-3 flex items-start gap-2" data-testid="overfit-warning">
                    <AlertTriangle className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div className="mono text-[11px] text-rose-100">
                      <strong className="text-rose-300">OVERFIT WARNING:</strong> {result.eligibility.warning}
                    </div>
                  </div>
                )}

                {/* Config-under-test: TRAIN | VALIDATION side-by-side */}
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    Config under test {loading && <span className="text-slate-600">(recomputing…)</span>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SplitPanel title="TRAIN split · first 2/3 sessions" stats={result.train}
                      testIdPrefix="split-train" tone="text-slate-400" />
                    <SplitPanel title="VALIDATION split · last 1/3 sessions" stats={result.validation}
                      testIdPrefix="split-validation" tone="text-cyan-300" highlight />
                  </div>
                </div>

                {/* Production (v1 baseline over same splits) */}
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                    v1 baseline (production) — same splits, no filters
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SplitPanel title="baseline · TRAIN" stats={result.production_train}
                      testIdPrefix="split-prod-train" tone="text-slate-500" />
                    <SplitPanel title="baseline · VALIDATION" stats={result.production_validation}
                      testIdPrefix="split-prod-validation" tone="text-slate-500" />
                  </div>
                </div>

                {/* Promotion */}
                <div className="border border-[#1E293B] bg-[#0E1523] p-4" data-testid="promote-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Promote to live</div>
                      <div className="mono text-[11px] text-slate-500 mt-1">
                        Applies this config to the live intraday engine + What Now scanner.
                      </div>
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest">
                      Gate: val_n≥{result.eligibility?.gates?.min_val_signals} · val_pf≥{result.eligibility?.gates?.min_val_pf} · val_exp&gt;0 · val_spd≥{result.eligibility?.gates?.min_val_signals_per_day}
                    </div>
                  </div>
                  {!result.eligibility?.eligible && (
                    <ul className="mono text-[11px] text-rose-300 space-y-1 mb-3" data-testid="ineligible-reasons">
                      {(result.eligibility?.reasons || []).map((r, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-rose-500">•</span><span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button onClick={promote} disabled={!result.eligibility?.eligible || promoting}
                    data-testid="promote-button"
                    className={`w-full mono text-[11px] uppercase tracking-widest py-2 flex items-center justify-center gap-2 border ${
                      result.eligibility?.eligible
                        ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                        : "border-slate-700 bg-slate-800/30 text-slate-500 cursor-not-allowed"
                    }`}>
                    <Rocket className="h-3 w-3" />
                    {promoting ? "Promoting…"
                      : result.eligibility?.eligible ? "Promote this config to live"
                      : "Not promotable"}
                  </button>
                </div>
              </>
            )}

            {/* History */}
            <div className="border border-[#1E293B] bg-[#0E1523] p-4" data-testid="config-history">
              <div className="flex items-center justify-between mb-3">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Config History (append-only)</div>
                <div className="mono text-[10px] text-slate-500">v1 baseline is always available</div>
              </div>
              {history.length === 0 && (
                <div className="mono text-[11px] text-slate-500">
                  No configs promoted yet. Currently running v1 baseline (intraday_engine 1.0.0).
                </div>
              )}
              {history.length > 0 && (
                <div className="space-y-2">
                  {history.map((h, i) => (
                    <div key={h.version + i} className="border border-[#1E293B] p-2 flex items-center justify-between text-xs mono"
                         data-testid={`history-row-${i}`}>
                      <div className="flex-1">
                        <div className="text-cyan-300 font-bold">{h.version}</div>
                        <div className="text-slate-500 text-[10px]">
                          {new Date(h.promoted_at).toLocaleString()} · by {h.promoted_by}
                          {h.rollback_of && ` · rollback of ${h.rollback_of}`}
                        </div>
                      </div>
                      <div className="mono text-[10px] text-slate-400 mr-3">
                        VAL exp {fmtSigned(h.validation_metrics?.expectancy_r)} · PF {fmtNum(h.validation_metrics?.profit_factor, 2)} · n={h.validation_metrics?.signal_count ?? 0}
                      </div>
                      <button onClick={() => rollback(h.version)}
                        data-testid={`rollback-${i}`}
                        className="mono text-[10px] uppercase tracking-widest border border-slate-600 text-slate-300 hover:text-cyan-300 hover:border-cyan-500 px-2 py-1">
                        Restore
                      </button>
                    </div>
                  ))}
                  <button onClick={() => rollback("intraday_engine 1.0.0")}
                    data-testid="rollback-v1"
                    className="w-full mono text-[10px] uppercase tracking-widest border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500 py-1.5">
                    Roll back to v1 baseline (no config)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <ShadowComparison shadow={shadowData} activeConfig={activeConfig} />
      </div>
      <DisclaimerFooter />
    </div>
  );
}
